import * as React from "react";
import { useAction } from "convex/react";
import { AlertTriangle, Check, ExternalLink, MapPin, RefreshCw, Truck } from "lucide-react";

import { api } from "$convex/convexApi";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type TrackingEvent = {
  createdDate?: string;
  eventDate?: string;
  eventTime?: string;
  eventType?: string;
  summary?: string;
  location?: string;
};

type TrackingLookupResult = {
  success?: boolean;
  error?: string;
  trackingNumber?: string;
  carrier?: string;
  carrierCode?: string;
  events?: TrackingEvent[];
};

type TrackingInfo = {
  trackingNumber?: string;
  carrier?: string;
  carrierCode?: string;
};

type TrackingEventsDialogTriggerProps = {
  orderId?: string | null;
  trackingNumber?: string | null;
  carrierName?: string | null;
  carrierCode?: string | null;
  trackingUrl?: string | null;
  triggerClassName?: string;
  triggerLabel?: React.ReactNode;
  dialogTitle?: string;
  autoFetchOnMount?: boolean;
  onDelivered?: (result: TrackingLookupResult) => Promise<void> | void;
};

function normalizeTrackingText(value?: string | null): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function hasDeliveredTrackingEvent(events: TrackingEvent[]): boolean {
  return events.some((event) => {
    const eventType = normalizeTrackingText(event.eventType);
    const summary = normalizeTrackingText(event.summary);

    return (
      eventType === "delivered" ||
      /\bdelivered\b/.test(eventType) ||
      /\bdelivered\b/.test(summary) ||
      /\bdelivery (complete|completed|confirmed|made)\b/.test(summary) ||
      /\bsigned for\b/.test(summary)
    );
  });
}

function formatTrackingTime(eventDate?: string, eventTime?: string): string {
  if (!eventDate) return "";
  if (!eventTime) return eventDate;
  try {
    const [rawHours, rawMinutes] = eventTime.split(":").map(Number);
    const hours = Number(rawHours ?? 0);
    const minutes = Number(rawMinutes ?? 0);
    const date = new Date(eventDate);
    date.setHours(hours, minutes, 0, 0);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return `${eventDate} ${eventTime}`;
  }
}

function formatDateTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function TrackingEventsDialogTrigger({
  orderId,
  trackingNumber,
  carrierName,
  carrierCode,
  trackingUrl,
  triggerClassName,
  triggerLabel,
  dialogTitle = "Live Tracking",
  autoFetchOnMount = false,
  onDelivered,
}: TrackingEventsDialogTriggerProps) {
  const getTrackingEventsForOrder = useAction((api as any).shipping.getTrackingEvents);
  const getTrackingEventsByNumber = useAction((api as any).shipping.getTrackingEventsByNumber);

  const normalizedOrderId = React.useMemo(() => String(orderId ?? "").trim(), [orderId]);
  const normalizedTrackingNumber = React.useMemo(
    () => String(trackingNumber ?? "").trim(),
    [trackingNumber],
  );
  const normalizedCarrierName = React.useMemo(() => String(carrierName ?? "").trim(), [carrierName]);
  const normalizedCarrierCode = React.useMemo(
    () => String(carrierCode ?? "").trim().toLowerCase(),
    [carrierCode],
  );

  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasFetched, setHasFetched] = React.useState(false);
  const [lastFetch, setLastFetch] = React.useState<number | null>(null);
  const [trackingError, setTrackingError] = React.useState<string | null>(null);
  const [trackingEvents, setTrackingEvents] = React.useState<TrackingEvent[]>([]);
  const [trackingInfo, setTrackingInfo] = React.useState<TrackingInfo | null>(null);
  const deliveredCallbackReportedRef = React.useRef(false);

  const displayTrackingNumber = trackingInfo?.trackingNumber || normalizedTrackingNumber;
  const displayCarrier = trackingInfo?.carrier || normalizedCarrierName;
  const hasTrackingContext = Boolean(normalizedOrderId || normalizedTrackingNumber);

  React.useEffect(() => {
    deliveredCallbackReportedRef.current = false;
  }, [normalizedOrderId, normalizedTrackingNumber]);

  const fetchTracking = React.useCallback(async () => {
    if (!hasTrackingContext) return;
    setIsLoading(true);
    setTrackingError(null);

    try {
      let result: TrackingLookupResult | null = null;
      if (normalizedOrderId) {
        result = await getTrackingEventsForOrder({ orderId: normalizedOrderId as any });
      } else {
        result = await getTrackingEventsByNumber({
          trackingNumber: normalizedTrackingNumber,
          carrierCode: normalizedCarrierCode || undefined,
          carrierName: normalizedCarrierName || undefined,
        });
      }

      if (result?.success) {
        const events = Array.isArray(result.events) ? result.events : [];
        setTrackingEvents(events);
        setTrackingInfo({
          trackingNumber: result.trackingNumber || normalizedTrackingNumber,
          carrier: result.carrier || normalizedCarrierName || undefined,
          carrierCode: result.carrierCode || normalizedCarrierCode || undefined,
        });
        if (
          onDelivered &&
          !deliveredCallbackReportedRef.current &&
          hasDeliveredTrackingEvent(events)
        ) {
          try {
            await onDelivered({ ...result, events });
            deliveredCallbackReportedRef.current = true;
          } catch (deliveryUpdateError) {
            console.error("Failed to handle delivered tracking event", deliveryUpdateError);
          }
        }
      } else {
        setTrackingEvents([]);
        setTrackingError(result?.error || "Failed to fetch tracking information");
      }
    } catch (error: any) {
      setTrackingEvents([]);
      setTrackingError(error?.message || "Failed to fetch tracking information");
    } finally {
      setHasFetched(true);
      setLastFetch(Date.now());
      setIsLoading(false);
    }
  }, [
    getTrackingEventsByNumber,
    getTrackingEventsForOrder,
    hasTrackingContext,
    normalizedCarrierCode,
    normalizedCarrierName,
    normalizedOrderId,
    normalizedTrackingNumber,
    onDelivered,
  ]);

  React.useEffect(() => {
    if (!isOpen || hasFetched || !hasTrackingContext) return;
    void fetchTracking();
  }, [fetchTracking, hasFetched, hasTrackingContext, isOpen]);

  React.useEffect(() => {
    if (!autoFetchOnMount || hasFetched || !hasTrackingContext) return;
    void fetchTracking();
  }, [autoFetchOnMount, fetchTracking, hasFetched, hasTrackingContext]);

  if (!normalizedTrackingNumber) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <>
      <button
        type="button"
        className={cn(
          "inline-flex cursor-pointer items-center rounded-sm font-mono font-semibold text-xs text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          triggerClassName,
        )}
        onClick={() => setIsOpen(true)}
      >
        {triggerLabel ?? normalizedTrackingNumber}
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>
              Track shipment updates for {displayTrackingNumber}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void fetchTracking()}
                disabled={isLoading}
                className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
              >
                <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
                {isLoading ? "Refreshing..." : "Refresh"}
              </Button>
            </div>

            {displayTrackingNumber ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-muted px-3 py-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Tracking #:</span>
                  <span className="font-mono font-medium">{displayTrackingNumber}</span>
                </div>
                {displayCarrier ? (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Carrier:</span>
                    <span className="font-medium">{displayCarrier}</span>
                  </div>
                ) : null}
              </div>
            ) : null}

            {isLoading && trackingEvents.length === 0 ? (
              <div className="space-y-3">
                {[1, 2, 3].map((skeletonId) => (
                  <div key={skeletonId} className="flex gap-4">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {!isLoading && trackingError ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                <AlertTriangle className="mx-auto mb-2 h-5 w-5 text-amber-600" />
                <p className="text-sm text-amber-800">{trackingError}</p>
                {trackingUrl ? (
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-sm text-amber-700 underline hover:text-amber-900"
                  >
                    Track on carrier website
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ) : null}
              </div>
            ) : null}

            {!isLoading && !trackingError && trackingEvents.length === 0 ? (
              <div className="py-6 text-center">
                <Truck className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No tracking updates yet</p>
                {trackingUrl ? (
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    Track on carrier website
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ) : null}
              </div>
            ) : null}

            {!isLoading && !trackingError && trackingEvents.length > 0 ? (
              <div className="relative -mt-2">
                <div className="max-h-56 overflow-y-auto">
                  <div className="relative pl-6">
                    <div className="space-y-3 pb-6">
                      {trackingEvents.map((event, index) => {
                        const isLast = index === trackingEvents.length - 1;
                        return (
                          <div
                            key={`${event.createdDate ?? event.eventDate ?? "event"}-${index}`}
                            className="relative flex items-start gap-3"
                          >
                            {!isLast ? (
                              <div className="absolute bottom-[-12px] left-[-15px] top-[22px] w-0.5 bg-primary/30" />
                            ) : null}
                            <div className="absolute -left-6 z-10 mt-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-primary bg-primary text-primary-foreground">
                              <Check className="h-2.5 w-2.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={cn("text-sm font-medium", index === 0 ? "text-foreground" : "text-muted-foreground")}>
                                {event.summary || "Tracking update"}
                              </p>
                              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                                <span>{formatTrackingTime(event.eventDate, event.eventTime)}</span>
                                {event.location ? (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {event.location}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {trackingEvents.length > 4 ? (
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background via-background/90 to-transparent" />
                ) : null}
                {lastFetch ? (
                  <p className="mt-4 text-center text-xs text-muted-foreground">
                    Last updated: {formatDateTime(lastFetch)}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
